package com.snakesworld.game.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.snakesworld.game.network.AuthState
import com.snakesworld.game.network.MultiplayerRepository
import com.snakesworld.game.network.UserProfile
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class AuthUiState(
    val authState: AuthState = AuthState.Uninitialized,
    val userProfile: UserProfile? = null,
    val isReadyToPlay: Boolean = false,
    /** Single Player can continue when anonymous sign-in fails. */
    val allowOfflinePlay: Boolean = false,
)

class AuthViewModel(
    private val repository: MultiplayerRepository,
    private val customScope: CoroutineScope? = null,
) : ViewModel() {

    private val scope: CoroutineScope
        get() = customScope ?: viewModelScope

    private val _uiState = MutableStateFlow(AuthUiState())
    val uiState: StateFlow<AuthUiState> = _uiState.asStateFlow()

    init {
        scope.launch {
            repository.authState.collect { state ->
                _uiState.value = _uiState.value.copy(
                    authState = state,
                    isReadyToPlay = state is AuthState.Authenticated || _uiState.value.allowOfflinePlay,
                )
            }
        }
        initializeAndSignIn()
    }

    /** Leave the splash screen and play Single Player without an account. */
    fun continueOffline() {
        _uiState.value = _uiState.value.copy(allowOfflinePlay = true, isReadyToPlay = true)
    }

    fun initializeAndSignIn() {
        scope.launch {
            val result = repository.initializeAndSignInAnonymously()
            result.onSuccess { uid ->
                val profile = repository.getUserProfile(uid) ?: UserProfile(
                    uid = uid,
                    nickname = "PLAYER_${uid.takeLast(4)}",
                )
                _uiState.value = _uiState.value.copy(userProfile = profile)
            }
        }
    }

    /**
     * Erase Firestore / local account documents first (while UID and auth are still valid),
     * then sign out, then reset this ViewModel. Navigation teardown must wait for success.
     */
    suspend fun clearAllUserData(): Result<Unit> {
        val uid = (repository.authState.value as? AuthState.Authenticated)?.uid.orEmpty()
        if (uid.isEmpty()) {
            return Result.failure(IllegalStateException("No authenticated user to clear"))
        }
        val deleteResult = repository.clearUserData(uid)
        if (deleteResult.isFailure) {
            return deleteResult
        }
        _uiState.value = AuthUiState()
        return Result.success(Unit)
    }
}
